import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Position, Tooltip as BPTooltip } from '@blueprintjs/core';
import cx from 'classnames';
import { Slider, Rail, Handles, Tracks } from 'react-compound-slider';
import { Handle, Track, Markers } from '.';
import { convertToTime, pauseIcon, playIcon, stopIcon } from './helpers';
import { InlineDropDown } from '..';
import { convertToMilliseconds, convertToSeconds } from './utils';
import { Icons } from '../Icons';
import { getConvertedDistance, getConvertedVerticalDistance } from '../../utils';
const noop = () => { };
export const PlaybackControls = ({ startTime, endTime, playbackStartTime = 0, markerLocations = [], timeZone = 'local', isDisabled = false, alwaysShowDot = false, onPlayToggle = noop, onPositionUpdate = noop, onSpeedUpdate = noop, onDragUpdate = noop, onPlaybackTypeToggle = noop, onPlaybackStop = () => { }, isFullControl = true, startOnLoad = true, className, profileData, showProfileData = false, canToggleModes = false, showAudio = false, hasAudio = false, audioTooltipText, operationStartTime, units }) => {
    const items = [
        {
            key: 'speed1',
            label: '1x',
        },
        {
            key: 'speed2',
            label: '2x',
        },
        {
            key: 'speed4',
            label: '4x',
        },
        {
            key: 'speed8',
            label: '8x',
        },
        {
            key: 'speed16',
            label: '16x',
        },
        {
            key: 'speed32',
            label: '32x',
        },
        {
            key: 'speed64',
            label: '64x',
        },
    ];
    const [selectedItem, setSelectedItem] = useState(items[0]);
    useEffect(() => {
        handleSpeedChange(Number(selectedItem.key.replace(/speed/g, '')));
    }, [selectedItem]);
    const startTimeMs = convertToMilliseconds(startTime);
    const endTimeMs = convertToMilliseconds(endTime);
    const playbackStartTimeMs = convertToMilliseconds(playbackStartTime);
    const [isPlaying, setIsPlaying] = useState(startOnLoad);
    const [isDragging, setIsDragging] = useState(false);
    const [currentTimeMs, setCurrentTimeMs] = useState(playbackStartTimeMs ? playbackStartTimeMs : startTimeMs);
    // Used to check if the rail or handle on the slider has been hovered on.
    const [isHoveringOverSlider, setIsHoveringOverSlider] = useState(false);
    // Used to keep track on if requestAnimationFrame has been called. If we should try to start the
    // timer again, it will not requestAnimationFrame again.
    const isRunningRef = useRef(false);
    const requestRef = useRef(-1);
    const previousTimeRef = useRef(-1);
    const transitionSpeedRef = useRef(1);
    const startOffset = operationStartTime ? startTime - operationStartTime : 0;
    const timeSinceStart = Math.round(convertToSeconds(currentTimeMs - startTimeMs) + startOffset);
    const currProfile = showProfileData
        ? profileData[timeSinceStart]
        : { dist: 0, alt: 0 };
    const isMountedRef = useRef(false);
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);
    const incrementTime = (timestamp) => {
        if (previousTimeRef.current > 0 && transitionSpeedRef.current > 0) {
            const deltaTime = Math.round(timestamp - previousTimeRef.current);
            // Pass a function to the setState to ensure we have the latest state
            setCurrentTimeMs((prevCount) => isMountedRef.current
                ? Math.min(prevCount + deltaTime * transitionSpeedRef.current, endTimeMs)
                : prevCount);
        }
        previousTimeRef.current = timestamp;
        if (currentTimeMs < endTimeMs && isRunningRef.current) {
            requestRef.current = requestAnimationFrame(incrementTime);
        }
        else {
            stopTimer();
        }
    };
    useEffect(() => {
        onPositionUpdate(convertToSeconds(currentTimeMs));
    }, [currentTimeMs]);
    useEffect(() => {
        if (currentTimeMs >= endTimeMs && isRunningRef.current) {
            stopTimer();
            setIsPlaying(false);
        }
    }, [currentTimeMs, endTime]);
    const startTimer = () => {
        if (isPlaying && !isRunningRef.current) {
            isRunningRef.current = true;
            requestRef.current = requestAnimationFrame(incrementTime);
        }
    };
    const stopTimer = () => {
        isRunningRef.current = false;
        cancelAnimationFrame(requestRef.current);
        previousTimeRef.current = -1;
    };
    useEffect(() => {
        if (isPlaying) {
            startTimer();
        }
        onPlayToggle(isPlaying);
        return () => cancelAnimationFrame(requestRef.current);
    }, [isPlaying, startTime, endTime]);
    useEffect(() => {
        if (isDisabled) {
            setCurrentTimeMs(startTimeMs);
            setIsPlaying(false);
            stopTimer();
        }
    }, [isDisabled]);
    const handlePlayToggle = (playing) => {
        if (!playing) {
            stopTimer();
        }
        if (playing && currentTimeMs >= endTimeMs) {
            previousTimeRef.current = -1;
            setCurrentTimeMs(startTimeMs);
        }
        setIsPlaying(playing);
    };
    const handleSliderUpdate = (value) => {
        setCurrentTimeMs(value[0]);
    };
    const handleSpeedChange = (value) => {
        onSpeedUpdate(value);
        transitionSpeedRef.current = value;
    };
    const onSlideStart = () => {
        setIsDragging(true);
        onDragUpdate(true);
        stopTimer();
    };
    const onSlideEnd = () => {
        setIsDragging(false);
        onDragUpdate(false);
        startTimer();
    };
    const sliderStyle = {
        // Give the slider some width
        position: 'relative',
        width: isFullControl ? '100% - 12px' : '100%',
        height: '4px',
        marginLeft: isFullControl ? '12px' : '16px',
        marginRight: isFullControl ? '12px' : '16px',
        marginTop: isFullControl ? 0 : '12px',
    };
    const railStyle = {
        position: 'absolute',
        width: '100%',
        height: 6,
        cursor: 'pointer',
        mixBlendMode: 'multiply',
        backgroundColor: isDisabled ? '#999' : '#c4cdd5',
    };
    const inline = useMemo(() => (React.createElement(InlineDropDown, { searchItems: items, updateSelection: setSelectedItem, selectedItem: selectedItem, isIconOnly: false, isNullable: false, hasIcon: false, mode: "dark", openPosition: "top-right" })), [selectedItem]);
    const playbackSlider = (React.createElement(Slider, { disabled: isDisabled, rootStyle: sliderStyle, domain: [startTimeMs, endTimeMs], step: 1, mode: 2, onUpdate: value => handleSliderUpdate(value), onSlideStart: () => onSlideStart(), onSlideEnd: () => onSlideEnd(), values: [currentTimeMs] },
        React.createElement(Rail, null, ({ getRailProps } // adding the rail props sets up events on the rail
        ) => (React.createElement("div", Object.assign({ className: "playback_rail", onMouseEnter: () => setIsHoveringOverSlider(true), onMouseLeave: () => setIsHoveringOverSlider(false), style: railStyle }, getRailProps())))),
        React.createElement(Handles, null, ({ handles, getHandleProps }) => (React.createElement("div", { className: "slider-handles" }, handles.map(handle => (React.createElement(Handle, { key: handle.id, handle: handle, getHandleProps: getHandleProps, disabled: isDisabled, showHandle: isDragging || isHoveringOverSlider || alwaysShowDot, onHover: setIsHoveringOverSlider })))))),
        React.createElement(Tracks, { right: false }, ({ tracks, getTrackProps }) => (React.createElement("div", { className: "slider-tracks" }, tracks.map(({ id, source, target }) => (React.createElement(Track, { key: id, source: source, target: target, getTrackProps: getTrackProps, disabled: isDisabled, onHover: setIsHoveringOverSlider })))))),
        markerLocations.map((marker, index) => (React.createElement(Markers, { key: `playback_marker_${index}`, startTime: startTime, endTime: endTime, markerTime: marker })))));
    const smallPlaybackControls = (React.createElement("div", { className: cx('playback-control_left', 'playback-control_container--small', className) },
        React.createElement("button", { className: "playback-control_button playback-control_button--dark", disabled: isDisabled, onClick: e => {
                e.stopPropagation();
                handlePlayToggle(!isPlaying);
            } }, isPlaying && !isDragging ? pauseIcon : playIcon),
        playbackSlider));
    const fullPlaybackControls = (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "playback-container_background" }, playbackSlider),
        React.createElement("div", { className: "playback-control playback-control_background" },
            React.createElement("div", { className: "playback-control_left" },
                React.createElement("button", { className: "playback-control_button", disabled: isDisabled, onClick: e => {
                        e.stopPropagation();
                        handlePlayToggle(!isPlaying);
                    } }, isPlaying && !isDragging ? pauseIcon : playIcon),
                React.createElement("button", { className: "playback-control_button", onClick: onPlaybackStop }, stopIcon),
                React.createElement("div", { className: "playback-control_text" },
                    React.createElement("div", { style: { width: '50px' } }, convertToTime(convertToSeconds(currentTimeMs), `${timeZone}`)),
                    React.createElement("div", { style: { width: '10px', textAlign: 'center' } }, "/"),
                    React.createElement("div", { style: { width: '50px' } }, convertToTime(endTime, `${timeZone}`)),
                    showProfileData && currProfile && (React.createElement(React.Fragment, null,
                        React.createElement("div", { className: "playback-control_text--profile" },
                            "Dist: ",
                            getConvertedDistance(currProfile.dist, units.distance, 0)),
                        React.createElement("div", { className: "playback-control_text--profile" },
                            "AMSL: ",
                            getConvertedVerticalDistance(currProfile.alt, units.distanceVertical, 0)))))),
            React.createElement("div", { className: "playback-control_text" },
                showAudio && (React.createElement("div", { className: cx('playback-control_audio', {
                        'playback-control_audio--unavailable': !hasAudio,
                    }) }, hasAudio ? (React.createElement(Icons, { iconName: `ic-ui-audio`, size: 18 })) : (React.createElement(BPTooltip, { content: audioTooltipText, position: Position.TOP },
                    React.createElement(Icons, { iconName: `ic-ui-no-audio`, size: 18 }))))),
                canToggleModes && (React.createElement("div", { className: "playback-control_toggle-container" },
                    React.createElement("div", { className: cx('playback-control_toggle', 'playback-control_toggle--left', {
                            'playback-control_toggle--selected': showProfileData,
                        }), role: "button", "aria-label": "Show profile graph", onClick: () => onPlaybackTypeToggle(true) },
                        React.createElement(Icons, { iconName: `ic-ui-profile`, size: 18 })),
                    React.createElement("div", { className: cx('playback-control_toggle', 'playback-control_toggle--right', {
                            'playback-control_toggle--selected': !showProfileData,
                        }), role: "button", "aria-label": "Show noise sample graph", onClick: () => onPlaybackTypeToggle(false) },
                        React.createElement(Icons, { iconName: `ic-ui-graph`, size: 18 })))),
                React.createElement("div", { className: "playback-control_dropdown" }, inline)))));
    return isFullControl ? fullPlaybackControls : smallPlaybackControls;
};
//# sourceMappingURL=PlaybackControls.js.map